import React, { useState } from "react";
import { FiChevronRight } from "react-icons/fi";
import {
  MdMonitor, MdDeviceHub, MdTimeline, MdBugReport,
  MdStorage, MdSpeed, MdMemory, MdNetworkCheck,
  MdDns, MdList
} from "react-icons/md";
import { useNavigate } from "react-router-dom";
import { HiOutlineServer, HiOutlineChip } from "react-icons/hi";
import { TbContainer } from "react-icons/tb";

/* ─── tiny shared primitives ─── */
const Breadcrumb = ({ items }) => (
  <nav className="flex flex-wrap items-center gap-2 text-sm text-[#9a734c] mb-8">
    {items.map((item, i) => (
      <span key={i} className="flex items-center gap-2">
        {i > 0 && <FiChevronRight className="text-[14px]" />}
        {item.href
          ? <a className="hover:text-primary hover:underline" href={item.href}>{item.label}</a>
          : <span className="text-slate-900 dark:text-white">{item.label}</span>}
      </span>
    ))}
  </nav>
);

const SectionHeading = ({ icon, label, id, color = "primary/10 text-primary" }) => (
  <div className="flex items-center gap-3 mb-4">
    <div className={`w-8 h-8 rounded bg-${color} flex items-center justify-center`}>
      {icon}
    </div>
    <h2 id={id} className="text-2xl font-bold tracking-tight text-slate-900 dark:text-white scroll-mt-24">
      {label}
    </h2>
  </div>
);

const StepItem = ({ num, children }) => (
  <li className="flex gap-3 items-start">
    <span className="mt-0.5 min-w-[1.5rem] h-6 w-6 rounded-full bg-primary/10 text-primary text-xs font-bold flex items-center justify-center">{num}</span>
    <span className="text-slate-600 dark:text-slate-400 leading-relaxed">{children}</span>
  </li>
);

const MetricCard = ({ icon: Icon, label, desc }) => (
  <div className="flex gap-3 p-4 rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-sm hover:shadow-md transition-shadow">
    <div className="w-9 h-9 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0">
      <Icon className="text-lg" />
    </div>
    <div>
      <p className="text-sm font-semibold text-slate-800 dark:text-white">{label}</p>
      <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5 leading-snug">{desc}</p>
    </div>
  </div>
);

const TABS = ["Overview", "Instance"];

const TAB_DESCRIPTIONS = [
  { tab: "Overview", desc: "Displays key server health and performance metrics." },
  { tab: "Instance", desc: "Displays every Redis instance belonging to the selected server." },
];

export function renderRedis() {
  return <RedisComponent />;
}

function RedisComponent() {
  /* ─── main export ─── */
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(null); // null = show all

  const handleTabClick = (tab) => {
    setActiveTab(tab === activeTab ? null : tab);
  };

  const showSection = (tab) => activeTab === null || activeTab === tab;

  const visibleDescriptions = activeTab
    ? TAB_DESCRIPTIONS.filter(t => t.tab === activeTab)
    : TAB_DESCRIPTIONS;

  return (
    <main className="transition-opacity duration-300 space-y-16">

      {/* Breadcrumbs */}
      <Breadcrumb items={[
        { label: "Netraa APM" },
        { label: "Analyze & Explore" },
        { label: "Apps" },
        { label: "Infrastructure" },
        { label: "Redis" }
      ]} />

      {/* PAGE HEADING */}
      <div className="border-b border-gray-200 dark:border-gray-800 pb-4">
        <div className="flex flex-col lg:flex-row lg:items-start gap-5 mb-6">

          {/* LEFT SIDE */}
          <div className="flex-1 min-w-0">
            <h1 className="text-3xl lg:text-5xl font-extrabold text-text-main dark:text-white tracking-tight leading-tight">
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-orange-500 to-red-600">
                Redis
              </span>
              <span className="text-orange-500">:</span>{" "}
              Centralized Redis Monitoring
            </h1>

            <p className="mt-3 text-lg lg:text-xl text-slate-500 dark:text-slate-400 leading-relaxed font-light">
              The Redis Monitoring module in Netraa provides centralized visibility into the health, availability, and performance of all monitored Redis servers and instances.
            </p>
          </div>

          {/* RIGHT SIDE BUTTON */}
          <div className="flex items-center gap-3 ml-auto">
            <button
              onClick={() => {
                navigate("/agentsetup", { state: { scrollTo: "Infra" } });
              }}
              className="px-5 py-3 bg-orange-50 dark:bg-orange-950/20 border border-orange-200 dark:border-orange-800 text-orange-600 dark:text-orange-400 rounded-xl font-semibold whitespace-nowrap hover:bg-orange-100 dark:hover:bg-orange-900/30 transition-colors inline-flex items-center gap-2"
            >
              <FiChevronRight className="rotate-180 w-4 h-4" />
              Instrument a Redis
            </button>
          </div>

        </div>
        <div className="grid sm:grid-cols-3 gap-4 mt-6">
          <MetricCard icon={MdSpeed} label="Runtime Statistics" desc="Collects runtime statistics directly from Redis for accurate insights." />
          <MetricCard icon={MdTimeline} label="Real-Time Metrics" desc="Intuitive dashboards, real-time metrics, and historical charts." />
          <MetricCard icon={MdNetworkCheck} label="Performance Visibility" desc="Quickly identify performance bottlenecks, memory issues, client connection problems, and cache efficiency." />
        </div>
      </div>

      {/* SECTION: Accessing the Redis Page */}
      <section id="accessing-redis" className="scroll-mt-24">
        <SectionHeading
          icon={<HiOutlineServer className="text-xl" />}
          label="Accessing the Redis Page"
          id="accessing-redis"
          color="primary/10 text-primary"
        />
        <p className="text-slate-600 dark:text-slate-400 leading-relaxed mb-4">
          To view monitored servers in Netraa, follow these steps:
        </p>
        <ol className="space-y-3">
          <StepItem num={1}>
            <strong className="text-slate-800 dark:text-slate-200">Log In —</strong> Sign in to the Netraa platform using your credentials.
          </StepItem>
          <StepItem num={2}>
            <strong className="text-slate-800 dark:text-slate-200">Default Landing Page —</strong> Upon successful authentication, you will be automatically redirected to the Services page, which serves as the default landing page.
          </StepItem>
          <StepItem num={3}>
            <strong className="text-slate-800 dark:text-slate-200">Navigate to Redis —</strong> From the left-side menu bar, click on <strong className="text-slate-800 dark:text-slate-200">Infrastructure</strong>. Under Infrastructure, select <strong className="text-slate-800 dark:text-slate-200">Redis</strong>.
          </StepItem>
        </ol>
        {/* <img src={redis1Img} alt="Redis Page – Overview" className="mt-4 rounded-lg border border-slate-200 dark:border-white/10 shadow-md" /> */}
      </section>

      {/* SECTION: Exploring Redis in Netraa */}
      <section id="exploring-redis" className="scroll-mt-24 space-y-6">
        <SectionHeading
          icon={<MdDeviceHub className="text-xl" />}
          label="Exploring Redis in Netraa"
          id="exploring-redis"
          color="primary/10 text-primary"
        />
        <ol className="space-y-3">
          <StepItem num={1}>
            From the Redis Monitoring page, click on the desired <strong className="text-slate-800 dark:text-slate-200">Redis</strong> entry.
          </StepItem>
          <StepItem num={2}>
            This action opens the <strong className="text-slate-800 dark:text-slate-200">Redis server Details page</strong>.
          </StepItem>
        </ol>
        <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
          The Redis server Details page provides comprehensive insights into the selected Redis servers, including:
        </p>
        <div className="grid sm:grid-cols-3 gap-4">
          <MetricCard icon={MdSpeed} label="Health & Availability" desc="Health and availability metrics." />
          <MetricCard icon={MdTimeline} label="Metrics" desc="Key performance metrics for the selected Redis server." />
          <MetricCard icon={MdBugReport} label="Resource Utilization Trends" desc="Resource utilization trends for the selected Redis server." />
        </div>

        {/* ── Interactive Tab Strip ── */}
        <div>
          <p className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-3 uppercase tracking-wide">
            The Redis details page is organized into the following tabs:
          </p>
          <div className="flex flex-wrap gap-2">
            {TABS.map(tab => (
              <button
                key={tab}
                onClick={() => handleTabClick(tab)}
                className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold border transition-all duration-200 cursor-pointer
                  ${activeTab === tab
                    ? "bg-primary text-white border-primary shadow-sm shadow-primary/30"
                    : "bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-slate-700 hover:border-primary/50 hover:text-primary dark:hover:text-primary"
                  }`}
              >
                {tab}
              </button>
            ))}

            {/* Show All — only visible when a tab is active */}
            {activeTab !== null && (
              <button
                onClick={() => setActiveTab(null)}
                className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold border border-dashed border-orange-400 text-orange-500 bg-orange-50 dark:bg-orange-950/20 hover:bg-orange-100 dark:hover:bg-orange-900/30 transition-all duration-200"
              >
                Show All
              </button>
            )}
          </div>
        </div>

        {/* ── Tab Description Table (filtered) ── */}
        <div className="divide-y divide-slate-100 dark:divide-slate-800 rounded-xl border border-slate-200 dark:border-slate-800 overflow-hidden">
          {visibleDescriptions.map(({ tab, desc }) => (
            <div
              key={tab}
              className={`flex gap-4 px-5 py-3.5 transition-colors duration-200
                ${activeTab === tab
                  ? "bg-orange-50 dark:bg-orange-950/20"
                  : "bg-white dark:bg-slate-900 hover:bg-slate-50 dark:hover:bg-slate-800/50"
                }`}
            >
              <span className="min-w-[110px] text-sm font-semibold text-primary">{tab}</span>
              <span className="text-sm text-slate-600 dark:text-slate-400">{desc}</span>
            </div>
          ))}
        </div>
      </section>

      {/* SECTION: Overview */}
      {showSection("Overview") && (
        <section id="overview" className="scroll-mt-24 space-y-6" style={{ animation: "fadeIn 0.3s ease" }}>
          <SectionHeading
            icon={<MdMonitor className="text-xl" />}
            label="Overview"
            id="overview"
            color="primary/10 text-primary"
          />
          <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
            The Overview page provides a comprehensive dashboard of the selected Redis, presenting key health metrics in a single, consolidated view. It enables users to quickly access and evaluate:
          </p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <MetricCard icon={MdTimeline} label="Uptime" desc="Uptime of the selected Redis server." />
            <MetricCard icon={MdDeviceHub} label="Connected Clients" desc="Number of clients currently connected." />
            <MetricCard icon={MdBugReport} label="Blocked Clients" desc="Number of clients currently blocked." />
            <MetricCard icon={MdStorage} label="DB Keys" desc="Total number of keys stored in the database." />
            <MetricCard icon={MdList} label="Expired Keys" desc="Number of keys that have expired." />
            <MetricCard icon={MdList} label="Evicted Keys" desc="Number of keys evicted from memory." />
            <MetricCard icon={MdMemory} label="Memory Usage %" desc="Percentage of memory currently in use." />
            <MetricCard icon={MdNetworkCheck} label="Network" desc="Network throughput metrics for the server." />
            <MetricCard icon={MdTimeline} label="Key Trend Count" desc="Trend of key counts over time." />
            <MetricCard icon={MdDeviceHub} label="Clients Connected/Blocked" desc="Trend of connected and blocked clients." />
            <MetricCard icon={MdSpeed} label="Hit Rate" desc="Cache hit rate for the server." />
            <MetricCard icon={MdSpeed} label="Command Rate" desc="Rate of commands processed by the server." />
            <MetricCard icon={MdTimeline} label="Avg Time Spent by Command" desc="Average time spent executing each command." />
          </div>
          {/* <img src={redis2Img} alt="Redis Overview" className="mt-4 rounded-lg border border-slate-200 dark:border-white/10 shadow-md" /> */}
        </section>
      )}

      {/* SECTION: Instance */}
      {showSection("Instance") && (
        <section id="instance" className="scroll-mt-24 space-y-6" style={{ animation: "fadeIn 0.3s ease" }}>
          <SectionHeading
            icon={<HiOutlineChip className="text-xl" />}
            label="Instance"
            id="instance"
            color="primary/10 text-primary"
          />
          <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
            This view shows the number of instances associated with the server and each server is shown with the metrics of connected clients, DB keys, Expired Keys, Memory usage and uptime.
          </p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <MetricCard icon={MdDeviceHub} label="Connected Clients" desc="Number of clients currently connected to the instance." />
            <MetricCard icon={MdStorage} label="DB Keys" desc="Total number of keys stored in the instance database." />
            <MetricCard icon={MdList} label="Expired Keys" desc="Number of keys that have expired on the instance." />
            <MetricCard icon={MdMemory} label="Memory Usage" desc="Memory usage of the instance." />
            <MetricCard icon={MdTimeline} label="Uptime" desc="Uptime of the instance." />
          </div>
          <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
            Upon clicking on the desired instance you will be able to see the below dashboard with the metrics.
          </p>
          {/* <img src={redis3Img} alt="Redis Instance List" className="mt-4 rounded-lg border border-slate-200 dark:border-white/10 shadow-md" />
          <img src={redis4Img} alt="Redis Instance Detail" className="mt-4 rounded-lg border border-slate-200 dark:border-white/10 shadow-md" /> */}
        </section>
      )}

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(8px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>

    </main>
  );
}